import curry from 'lodash/curry';
import { ChannelOptNodeData } from 'modules/workflows/types';
import { constructChildNode } from 'modules/workflows/utils';
import { ChannelOption } from './types';
import { channelOptions } from './ChannelOpt.constants';

/**
 * Generates the channel option selects, using the child nodes to determine selected state
 */
export const getChannelOptions = ({
  childNodes = [],
}: Partial<ChannelOptNodeData> = {}): ChannelOption[] => {
  const selectedOptions = childNodes.map(({ type }) => type);
  return channelOptions.map((o) => ({ ...o, isChecked: selectedOptions.includes(o.nodeType) }));
};

type OptionMap = Record<string, ChannelOption>;

/**
 * Creates a list of currently channels, ordered by their priority in the flowchart.
 */
export const getChannelPriorities = (
  options: ChannelOption[] = [],
  { childNodes = [] }: Partial<ChannelOptNodeData> = {},
): ChannelOption[] => {
  const optionMap: OptionMap = options.reduce((a, o) => ({ ...a, [o.nodeType]: o }), {});
  return childNodes.map(({ type }) => optionMap[type as string]);
};

/**
 * Updates the nodeData to match what was configured in the editor
 */
export const parseNodeDataForSaving = ({
  nodeData: { childNodes = [], ...nodeData } = {},
  channels = [],
}: {
  nodeData: Partial<ChannelOptNodeData>;
  channels: ChannelOption[];
}) => {
  const nodeMap = childNodes.reduce((a, node) => ({ ...a, [node.type]: node }), {});
  const updatedChildNodes = channels.map(
    (c) => nodeMap[c.nodeType] ?? constructChildNode({ key: c.channelType, value: {} }),
  );

  return { ...nodeData, childNodes: updatedChildNodes };
};

/**
 * @params currentIndex Number Index representing the index of current element in iteration
 * @params sourceIndex Number Index representing the starting index of the moving element
 * @params targetIndex Number Index representing the ending index of the moving element
 * @returns Number
 * @description Calculate the next placement index for a given element
 * provided a moving element's source and target positions
 */
export const getNextIndex = (
  currentIndex: number,
  sourceIndex: number,
  targetIndex: number,
): number => {
  if (currentIndex === sourceIndex) {
    return targetIndex;
  }
  // inside bounds
  const outsideTarget = currentIndex < targetIndex;
  const outsideSource = currentIndex > sourceIndex;
  const isTarget = currentIndex === targetIndex;
  if ((outsideTarget ? !outsideSource : outsideSource) && !isTarget) {
    return currentIndex;
  }

  // going up
  if (sourceIndex > targetIndex) {
    return currentIndex + 1;
  }
  // going down
  if (sourceIndex <= targetIndex) {
    return currentIndex - 1;
  }
  return currentIndex;
};

/**
 * @params sourceIndex Number Index representing the starting index of the moving element
 * @params targetIndex Number Index representing the ending index of the moving element
 * @params prevState Array<ChannelOption> The previous state of priorities to iterate over
 * @returns Array<ChannelOption>
 */
export const updateOrder = curry(
  (
    sourceIndex: number,
    targetIndex: number,
    prevState: Array<ChannelOption>,
  ): Array<ChannelOption> => {
    return prevState.reduce(
      (nextState, _, index: number) => {
        const nextIndex = getNextIndex(index, sourceIndex, targetIndex);
        nextState.splice(nextIndex, 0, prevState[index]);
        return nextState;
      },
      [] as Array<ChannelOption>,
    );
  },
);

/**
 * @params selectionId String Id of the clicked channel option
 * @params prevState Array<ChannelOption> The previous state of channels to iterate over
 * @returns Array<ChannelOption>
 */
export const createNextChannels = curry(
  (selectionId: string, prevState: Array<ChannelOption>): Array<ChannelOption> => {
    return prevState.reduce((channels: Array<ChannelOption>, current: ChannelOption) => {
      if (current.id === selectionId) {
        return channels.concat({ ...current, isChecked: !current.isChecked });
      }
      return channels.concat(current);
    }, []);
  },
);

export const getPreviousChannelDetails = (
  channel: ChannelOption,
  allChannels: Array<ChannelOption>,
) =>
  allChannels.reduce(
    (channelDetails, current: ChannelOption, index: number): { index: number; channel: any } => {
      if (current.text === channel.text) {
        return {
          index,
          channel: current,
        };
      }
      return channelDetails;
    },
    {
      index: -1,
      channel: null,
    },
  );

/**
 * @params channels Array<ChannelOption> Current State of channels
 * @params prevState Array<ChannelOption> The previous state of priorities
 * @returns Array<ChannelOption>
 */
export const createNextPriorities = curry(
  (channels: Array<ChannelOption>, prevState: Array<ChannelOption>): Array<ChannelOption> => {
    return [...channels].reduce(
      (accChannels, channel) => {
        const { channel: prevChannel, index } = getPreviousChannelDetails(channel, accChannels);
        if (channel.isChecked && !prevChannel) {
          return [...accChannels, channel];
        }
        if (!channel.isChecked && prevChannel) {
          accChannels.splice(index, 1);
        }
        return accChannels;
      },
      [...prevState],
    );
  },
);

export const hasDeselectedChannels = (
  originalChannels: ChannelOption[],
  currentChannels: ChannelOption[],
) => {
  const originalSelectedChannels = originalChannels.filter((channel) => channel.isChecked);
  const hasDeselectedSomeChannels = originalSelectedChannels.some((selectedChannel) => {
    const matchingChannel = currentChannels.find(
      (currentChannel) => currentChannel.id === selectedChannel.id,
    );
    return matchingChannel?.isChecked !== selectedChannel.isChecked;
  });
  return hasDeselectedSomeChannels;
};
